'use client'

import { type Ref, useMemo, useState } from 'react'
import type {
  GroupBase,
  MultiValueProps,
  Props,
  SelectInstance,
} from 'react-select'
import { useStateManager } from 'react-select'
import { type AsyncProps, useAsync } from 'react-select/async'
import type { AsyncCreatableProps } from 'react-select/async-creatable'
import BaseSelect from 'react-select/base'
import { type CreatableProps, useCreatable } from 'react-select/creatable'
import Tag, { DismissibleTag } from '../Tag'
import type { SelectVariant } from './comboboxCommon'

function NoClearIndicator() {
  return null
}

function SelectionCountTag<
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>({
  variant = 'regular',
  ...props
}: MultiValueProps<Option, IsMulti, Group> & { variant?: SelectVariant }) {
  if (props.index !== 0) return null
  const count = props.getValue().length
  const label = <span className="min-w-4 text-center">{count}</span>
  const size = variant === 'slim' ? 'sm' : 'md'
  const canClear = props.selectProps.isClearable !== false

  return (
    <span
      className="combobox-selection-count mr-1 flex shrink-0"
      onMouseDown={(event) => {
        event.preventDefault()
        event.stopPropagation()
      }}
      onTouchEnd={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {canClear ? (
        <DismissibleTag
          color="blue"
          size={size}
          disabled={props.isDisabled}
          dismissTitle="Auswahl löschen"
          onDismiss={props.clearValue}
          className="[&>button]:ml-0 [&>span]:pr-2 [&_svg]:size-4"
        >
          {label}
        </DismissibleTag>
      ) : (
        <Tag color="blue" size={size}>
          {label}
        </Tag>
      )}
    </span>
  )
}

function useOrderedProps<
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(props: Props<Option, IsMulti, Group>, variant: SelectVariant) {
  const MultiValue = useMemo(
    () =>
      function CountTag(props: MultiValueProps<Option, IsMulti, Group>) {
        return <SelectionCountTag {...props} variant={variant} />
      },
    [variant],
  )
  const managed = useStateManager(props)
  const getOptionValue =
    props.getOptionValue ??
    ((option: Option) => (option as { value: string }).value)
  const selectedOptions = (): readonly Option[] =>
    Array.isArray(managed.value)
      ? managed.value
      : managed.value == null
        ? []
        : [managed.value as Option]
  const [selectedAtOpen, setSelectedAtOpen] = useState(selectedOptions)
  // Prepare the next opening while closed so react-select also focuses the
  // first option in the new order when the user opens with the keyboard.
  const selectedForOrder = managed.menuIsOpen
    ? selectedAtOpen
    : selectedOptions()
  const options = useMemo(() => {
    if (!props.isMulti || !managed.options) return managed.options
    const selectedValues = new Set(selectedForOrder.map(getOptionValue))
    const isSelected = (option: Option) =>
      selectedValues.has(getOptionValue(option))
    const sort = (options: readonly Option[]) => [
      ...options.filter(isSelected),
      ...options.filter((option) => !isSelected(option)),
    ]
    // Preserve group labels and the relative order inside each selection bucket.
    const sorted = managed.options
      .map((option) =>
        'options' in (option as Group)
          ? { ...option, options: sort((option as Group).options) }
          : option,
      )
      .sort((a, b) => {
        const hasSelection = (option: Option | Group) =>
          'options' in (option as Group)
            ? (option as Group).options.some(isSelected)
            : isSelected(option as Option)
        return Number(hasSelection(b)) - Number(hasSelection(a))
      })
    if (managed.inputValue) return sorted
    const loadedValues = new Set(
      managed.options
        .flatMap((option) =>
          'options' in (option as Group)
            ? (option as Group).options
            : [option as Option],
        )
        .map(getOptionValue),
    )
    return [
      ...selectedForOrder.filter(
        (option) => !loadedValues.has(getOptionValue(option)),
      ),
      ...sorted,
    ]
  }, [
    managed.options,
    managed.inputValue,
    props.isMulti,
    selectedForOrder,
    getOptionValue,
  ])

  return {
    ...managed,
    options,
    components: props.isMulti
      ? {
          ...managed.components,
          MultiValue,
          ClearIndicator: NoClearIndicator,
        }
      : managed.components,
    onMenuOpen: () => {
      setSelectedAtOpen(selectedOptions())
      managed.onMenuOpen()
    },
  }
}

type SelectRef<
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
> = {
  ref?: Ref<SelectInstance<Option, IsMulti, Group>>
  variant?: SelectVariant
}

export function Select<
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  ref,
  variant = 'regular',
  ...props
}: Props<Option, IsMulti, Group> & SelectRef<Option, IsMulti, Group>) {
  return <BaseSelect {...useOrderedProps(props, variant)} ref={ref} />
}

export function AsyncSelect<
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  ref,
  variant = 'regular',
  ...props
}: AsyncProps<Option, IsMulti, Group> & SelectRef<Option, IsMulti, Group>) {
  return <BaseSelect {...useOrderedProps(useAsync(props), variant)} ref={ref} />
}

export function CreatableSelect<
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  ref,
  variant = 'regular',
  ...props
}: Omit<CreatableProps<Option, IsMulti, Group>, 'allowCreateWhileLoading'> &
  SelectRef<Option, IsMulti, Group>) {
  return (
    <BaseSelect
      {...useCreatable({
        ...useOrderedProps(props, variant),
        allowCreateWhileLoading: true,
      })}
      ref={ref}
    />
  )
}

export function AsyncCreatableSelect<
  Option = unknown,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  ref,
  variant = 'regular',
  ...props
}: Omit<
  AsyncCreatableProps<Option, IsMulti, Group>,
  'allowCreateWhileLoading'
> &
  SelectRef<Option, IsMulti, Group>) {
  return (
    <BaseSelect
      {...useCreatable({
        ...useOrderedProps(useAsync(props), variant),
        allowCreateWhileLoading: true,
      })}
      ref={ref}
    />
  )
}
